import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import { UUID } from '@/types/types';
import { Product } from '@/api/ProductApi';
import { ProductType } from '@/api/ProductTypeApi';
import { getFileSrc } from '@/utils/utilities';
import { AppPaths } from '@/configs/appPaths';
import APP_CONFIG from '@/configs/appConfig';

export interface CartProduct {
	id: UUID;
	productTypeId: UUID;
	productName: string;
	name: string;
	translations: ProductType['translations'];
	imagePath?: string;
}

interface CartItem {
	id: UUID;
	selected: boolean;
	quantity: number;
	cartProduct: CartProduct;
}

export interface CartState {
	items: CartItem[];
	lastEditedItemId: UUID | null;
}

const initialState: CartState = {
	items: [],
	lastEditedItemId: null,
};

function syncStorage(state: CartState, updater: (state: CartState) => void) {
	updater(state);
	localStorage.setItem(
		APP_CONFIG.STORAGE_KEYS.CART,
		JSON.stringify(current(state))
	);
}

export const cartSlice = createSlice({
	name: StoreKey.CART,
	initialState,
	reducers: {
		addCartItem: (
			state,
			{ payload }: PayloadAction<{ product: Product; productType: ProductType }>
		) => {
			syncStorage(state, () => {
				state.items.push({
					id: payload.product.id,
					selected: true,
					quantity: 1,
					cartProduct: {
						id: payload.product.id,
						productTypeId: payload.productType.id,
						productName: payload.product.name,
						name: payload.productType.name,
						translations: payload.productType.translations,
						imagePath: getFileSrc(payload.product.image?.path),
					},
				});
			});
		},
		removeCartItem: (state, action: PayloadAction<UUID>) => {
			syncStorage(state, () => {
				state.items = state.items.filter((item) => item.id !== action.payload);
			});
		},
		updateItem: (
			state,
			action: PayloadAction<{ id: UUID; payload: Partial<CartItem> }>
		) => {
			syncStorage(state, () => {
				const item = state.items.find((item) => item.id === action.payload.id);
				if (item) Object.assign(item, action.payload.payload);
			});
		},
		clearCart: (state) => {
			syncStorage(state, () => {
				state.items = state.items.filter((item) => !item.selected);
			});
		},
		setLastEditedItem: (state, action: PayloadAction<UUID | null>) => {
			syncStorage(state, () => {
				state.lastEditedItemId = action.payload;
			});
		},
	},
});

export const cartActions = cartSlice.actions;

export const useGetLastItemLink = (state: CartState) => {
	const item = state.items.find((item) => item.id === state.lastEditedItemId);

	if (item)
		return `/${AppPaths.CONFIGURATION}/${item.cartProduct.id}?productType=${item.cartProduct.productTypeId}`;

	return `/${AppPaths.PRODUCT_TYPE_CATEGORIES}`;
};

export const selectCartState = (state: RootState) => state.cart;
export default cartSlice.reducer;
